/**
 * PDFExporter — Generates PDF summaries of glass configurations.
 * Client-side PDF generation using jsPDF.
 */
import { jsPDF } from 'jspdf';

export default class PDFExporter {
  /**
   * Generate and download a PDF of the current order positions.
   *
   * @param {Object} params
   * @param {Array} params.positions - Array of position objects
   * @param {Object} params.user - { email, name, company }
   * @param {Object} params.formData - { kommission, notiz, offerteBis, lieferungBis }
   */
  generate({ positions, user, formData }) {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = margin;

    // ── Header ──
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('CDR Glas Konfigurator', margin, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Angebotsanfrage', margin, y);
    doc.text(new Date().toLocaleDateString('de-CH'), pageW - margin, y, { align: 'right' });
    y += 8;

    // Line
    doc.setDrawColor(200);
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    // ── Customer info ──
    doc.setTextColor(0);
    doc.setFontSize(10);
    const infoLines = [
      ['Kunde', user?.name || '—'],
      ['Firma', user?.company || '—'],
      ['E-Mail', user?.email || '—'],
    ];
    if (formData?.kommission) infoLines.push(['Kommission', formData.kommission]);
    if (formData?.offerteBis) infoLines.push(['Offerte bis', formData.offerteBis]);
    if (formData?.lieferungBis) infoLines.push(['Lieferung bis', formData.lieferungBis]);

    infoLines.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label + ':', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), margin + 35, y);
      y += 5;
    });

    if (formData?.notiz) {
      y += 2;
      doc.setFont('helvetica', 'bold');
      doc.text('Notiz:', margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      const notizLines = doc.splitTextToSize(formData.notiz, contentW);
      doc.text(notizLines, margin, y);
      y += notizLines.length * 4.5;
    }

    y += 5;
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    // ── Positions ──
    let grandTotalSqm = 0;
    let grandTotalKg = 0;

    positions.forEach((pos, posIdx) => {
      // Check if we need a new page
      if (y > 250) {
        doc.addPage();
        y = margin;
      }

      // Position header
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Position ${posIdx + 1}: ${pos.frgName || '—'}`, margin, y);
      y += 6;

      // Config details
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80);

      const configItems = [
        ['Aufbau', pos.structure],
        ['Klasse', pos.class],
        ['Isolation', pos.insulation],
        ['Anwendung', pos.application],
        ['Hersteller', pos.manufacturer],
        ['Widerstand', pos.resistance],
        ['Weissglas', pos.whiteGlass],
        ['Stossfugen', pos.buttJoints],
      ].filter(([, v]) => v && v !== '—' && v !== 'Nein' && v !== 'Alle');

      const configLine = configItems.map(([l, v]) => `${l}: ${v}`).join('  |  ');
      const configWrapped = doc.splitTextToSize(configLine, contentW);
      doc.text(configWrapped, margin, y);
      y += configWrapped.length * 3.5 + 2;

      // Glass names
      doc.setTextColor(60);
      const glassLine = [
        pos.frgName !== '—' ? `BSG: ${pos.frgName}` : null,
        pos.mgName !== '—' ? `MG: ${pos.mgName}` : null,
        pos.ogName !== '—' ? `AG: ${pos.ogName}` : null,
      ].filter(Boolean).join('  |  ');
      if (glassLine) {
        doc.text(glassLine, margin, y);
        y += 4;
      }

      doc.setTextColor(0);
      y += 2;

      // Variant table header
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      const cols = [
        { label: 'NR', x: margin, w: 10 },
        { label: 'B (mm)', x: margin + 12, w: 20 },
        { label: 'H (mm)', x: margin + 34, w: 20 },
        { label: 'STK', x: margin + 56, w: 12 },
        { label: 'M²/STK', x: margin + 70, w: 18 },
        { label: 'M²/POS', x: margin + 90, w: 18 },
        { label: 'KG/STK', x: margin + 110, w: 18 },
      ];

      // Header background
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, y - 3, contentW, 5, 'F');
      cols.forEach(c => doc.text(c.label, c.x, y));
      y += 5;

      // Variant rows
      doc.setFont('helvetica', 'normal');
      let posTotalQty = 0;
      let posTotalSqm = 0;
      let posTotalKg = 0;

      (pos.variants || []).forEach((v, vi) => {
        const qty = parseInt(v.quantity) || 1;
        const w = parseFloat(v.width) || 0;
        const h = parseFloat(v.height) || 0;
        const sqmStk = (w * h) / 1000000;
        const sqmPos = sqmStk * qty;
        const kgStk = sqmStk * (pos.weightPerSqm || 0);

        posTotalQty += qty;
        posTotalSqm += sqmPos;
        posTotalKg += kgStk * qty;

        const rowData = [
          `${posIdx + 1}.${vi + 1}`,
          w > 0 ? String(w) : '—',
          h > 0 ? String(h) : '—',
          String(qty),
          sqmStk > 0 ? sqmStk.toFixed(2) : '—',
          sqmPos > 0 ? sqmPos.toFixed(2) : '—',
          kgStk > 0 ? kgStk.toFixed(1) : '—',
        ];

        cols.forEach((c, ci) => doc.text(rowData[ci], c.x, y));
        y += 4;
      });

      // Position totals
      doc.setFont('helvetica', 'bold');
      doc.text('Gesamt', margin, y);
      doc.text(String(posTotalQty), cols[3].x, y);
      doc.text(posTotalSqm > 0 ? posTotalSqm.toFixed(2) : '—', cols[5].x, y);
      doc.text(posTotalKg > 0 ? posTotalKg.toFixed(1) : '—', cols[6].x, y);
      y += 6;

      grandTotalSqm += posTotalSqm;
      grandTotalKg += posTotalKg;

      // Separator
      doc.setDrawColor(220);
      doc.line(margin, y, pageW - margin, y);
      y += 6;
    });

    // ── Grand totals ──
    if (y > 260) {
      doc.addPage();
      y = margin;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Zusammenfassung', margin, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Positionen: ${positions.length}`, margin, y);
    y += 5;
    doc.text(`Gesamt m²: ${grandTotalSqm.toFixed(2)}`, margin, y);
    y += 5;
    doc.text(`Gesamt kg: ${grandTotalKg.toFixed(1)}`, margin, y);
    y += 10;

    // ── Footer ──
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text('CDR Glas AG — Konfigurator V2', margin, 285);
    doc.text(new Date().toLocaleString('de-CH'), pageW - margin, 285, { align: 'right' });

    // Download
    const filename = formData?.kommission
      ? `CDR_Anfrage_${formData.kommission}.pdf`
      : `CDR_Anfrage_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
  }
}
